/**
 * cart-add-merge.js
 *
 * Tier-aware cart add for bundled offer items.
 *
 * window.CartBundleAdd.smartAdd(items)
 *
 * Before adding bundled items, checks the cart for an existing "main" item
 * from the same tier (detected via the _bundleId prefix, e.g. "explorer_<ts>").
 *
 *  - Same tier already in cart → increment only the main item quantity, leave
 *    gifts completely untouched.
 *  - New tier (or empty cart)  → add all items (main + gifts) as normal.
 *
 * After any successful mutation, publishes theme.pubsub cartUpdate WITH sections
 * HTML (same payload as ProductForm) so CartItems updates the drawer DOM, then
 * calls cartDrawer.show() directly — the exact same path as the native theme.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Network helpers
  // ---------------------------------------------------------------------------

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('[CartBundleAdd] /cart.js ' + r.status);
      return r.json();
    });
  }

  /**
   * Collect the section IDs that CartDrawer (and other listeners) need bundled
   * into the /cart/change.js response — mirrors what CartItems.updateQuantity does.
   * Returns an array of section ID strings (may be empty if no listeners).
   */
  function collectBundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function updateCartCountBadge(count) {
    var parsed = Number(count);
    var safeCount = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    document.querySelectorAll('cart-count').forEach(function (el) {
      el.innerText = String(safeCount);
      el.hidden = safeCount === 0;
    });
  }

  /**
   * The FIRST `[aria-controls="CartDrawer"]` in the document is often the
   * overlay inside `<cart-drawer>` (see cart-drawer.liquid), NOT the header
   * cart link. Passing that to `show()` breaks opening — the real icon works.
   * Prefer the actual cart triggers (header, mobile dock), then fall back.
   */
  function getCartDrawerToggleControl() {
    return (
      document.querySelector('a.cart-drawer-button[aria-controls="CartDrawer"]') ||
      document.querySelector('a.dock__item[aria-controls="CartDrawer"]') ||
      document.querySelector('a[href*="cart"][aria-controls="CartDrawer"]') ||
      document.querySelector('button[aria-controls="CartDrawer"]') ||
      document.querySelector('[aria-controls="CartDrawer"]')
    );
  }

  /**
   * Same path as clicking the real cart icon: `onButtonClick` →
   * `this.open ? hide() : show(event.currentTarget)`.
   * Uses the correct control element + `.click()` so behavior matches the user.
   */
  async function openCartDrawerLikeHeader() {
    var cartDrawer = getPreferredCartDrawer();
    var ctrl = getCartDrawerToggleControl();
    if (!cartDrawer) {
      return;
    }
    if (!ctrl) {
      if (typeof cartDrawer.show === 'function') cartDrawer.show(null, true);
      return;
    }

    if (typeof window.__TASTE_PROFILE_SUPPRESS_CART_DRAWER__ !== 'undefined' && window.__TASTE_PROFILE_SUPPRESS_CART_DRAWER__) {
      window.__TASTE_PROFILE_SUPPRESS_CART_DRAWER__ = false;
    }

    if (cartDrawer.style && cartDrawer.style.display === 'none') {
      cartDrawer.style.display = '';
    }

    if (cartDrawer.open) {
      try {
        if (typeof cartDrawer.hide === 'function') {
          await Promise.race([
            cartDrawer.hide(),
            new Promise(function (resolve) { setTimeout(resolve, 600); }),
          ]);
        } else {
          cartDrawer.removeAttribute('open');
        }
      } catch (e) {
        cartDrawer.removeAttribute('open');
      }
    }

    ctrl.click();
  }

  function getPreferredCartDrawer() {
    var byId = document.getElementById('CartDrawer');
    if (byId && byId.tagName && byId.tagName.toLowerCase() === 'cart-drawer') return byId;

    var all = Array.prototype.slice.call(document.querySelectorAll('cart-drawer'));
    if (!all.length) return null;

    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden) return all[i];
    }
    return all[0];
  }

  /**
   * POST /cart/change.js with sections bundled so the response includes
   * pre-rendered section HTML — same as the theme's CartItems.updateQuantity.
   */
  function cartChange(key, quantity) {
    var sections = collectBundledSections();
    var body = { id: key, quantity: quantity };
    if (sections.length) body.sections = sections;

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('[CartBundleAdd] /cart/change.js ' + r.status);
      return r.json();
    });
  }

  // ---------------------------------------------------------------------------
  // Cart UI notification
  // ---------------------------------------------------------------------------

  /**
   * After a successful cart mutation, update the UI and open the cart drawer.
   *
   * Mirrors what ProductForm does after /cart/add.js:
   *   1. Publish theme.pubsub cartUpdate WITH sections so CartItems can update
   *      the drawer DOM without throwing (it needs sections[sectionId] HTML).
   *   2. Update cart-count badge directly as a safe fallback.
   *   3. Call cartDrawer.show() — the native show path that handles body classes,
   *      animations, and focus trapping.
   *
   * @param {Object} parsedState  Response from /cart/change.js or /cart/add.js
   *                              (may include parsedState.sections if bundled).
   */
  async function notifyCartUI(parsedState) {
    // Step 1: publish cartUpdate WITH sections so CartItems.onCartUpdate can
    // render the drawer HTML correctly (it reads event.cart.sections[sectionId]).
    // Isolated in try/catch because pubsub.publish has no internal error handling.
    try {
      if (
        typeof window.theme !== 'undefined' &&
        window.theme.pubsub &&
        typeof window.theme.pubsub.publish === 'function' &&
        window.theme.pubsub.PUB_SUB_EVENTS &&
        window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate &&
        parsedState
      ) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          cart: parsedState,
        });
      }
    } catch (e) {}

    // Step 2: update cart-count badge directly (safe fallback if pubsub failed).
    if (parsedState) {
      updateCartCountBadge(parsedState.item_count);
    }

    // Step 3: refresh drawer HTML only (no open:true — it races async show() with
    // our toggle click and used the wrong aria-controls target before). CartItems
    // already updated from pubsub; this keeps section markup in sync.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));

    // Step 4: open using the REAL cart icon (not overlay) — see getCartDrawerToggleControl.
    await new Promise(function (resolve) { setTimeout(resolve, 300); });
    await openCartDrawerLikeHeader();
  }

  // ---------------------------------------------------------------------------
  // Tier helpers
  // ---------------------------------------------------------------------------

  function getTierFromBundleId(bundleId) {
    if (!bundleId || typeof bundleId !== 'string') return '';
    return bundleId.split('_')[0];
  }

  function findExistingTierMain(cartItems, tier) {
    if (!tier) return null;
    for (var i = 0; i < cartItems.length; i++) {
      var li = cartItems[i];
      var props = li.properties || {};
      var bId = typeof props._bundleId === 'string' ? props._bundleId : '';
      var role = typeof props._role === 'string' ? props._role.toLowerCase() : '';
      if (role === 'main' && getTierFromBundleId(bId) === tier) return li;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // smartAdd
  // ---------------------------------------------------------------------------

  /**
   * @param {Array} items  Cart items with properties: { _bundleId, _role }.
   */
  async function smartAdd(items) {
    var mainItem = null;
    for (var i = 0; i < items.length; i++) {
      if ((items[i].properties || {})._role === 'main') {
        mainItem = items[i];
        break;
      }
    }

    // No recognised main item — add everything as-is.
    if (!mainItem) {
      var sections = collectBundledSections();
      var addBody = { items: items };
      if (sections.length) addBody.sections = sections;
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addBody),
      });
      if (!res.ok) throw new Error('[CartBundleAdd] /cart/add.js ' + res.status);
      var parsedAdd = await res.json();
      await notifyCartUI(parsedAdd);
      return res;
    }

    var bundleId = (mainItem.properties || {})._bundleId || '';
    var tier = getTierFromBundleId(bundleId);

    var cart = await fetchCart();
    var existingMain = findExistingTierMain(cart.items || [], tier);

    if (existingMain) {
      // Same tier: bump main quantity only, gifts stay untouched.
      var addQty = Number(mainItem.quantity) || 1;
      var newQty = existingMain.quantity + addQty;
      var parsedChange = await cartChange(existingMain.key, newQty);
      await notifyCartUI(parsedChange);
      return new Response(JSON.stringify({ updated: true, tier: tier, quantity: newQty }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // New tier or empty cart: add everything (main + gifts).
    var bundleSections = collectBundledSections();
    var newBundleBody = { items: items };
    if (bundleSections.length) newBundleBody.sections = bundleSections;
    var addRes = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(newBundleBody),
    });
    if (!addRes.ok) {
      var errText = '';
      try { errText = await addRes.text(); } catch (e) {}
      throw new Error('[CartBundleAdd] /cart/add.js ' + addRes.status + (errText ? ' ' + errText : ''));
    }
    var parsedNew = await addRes.json();
    await notifyCartUI(parsedNew);
    return addRes;
  }

  window.CartBundleAdd = { smartAdd: smartAdd, notifyCartUI: notifyCartUI };
})();
